import { FC, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { ValidatedRecording } from '../ertUtils'
import { isErtDebugModeEnabled, SCRUBBER_UPDATE_INTERVAL_MS } from '../rrweb/rrwebUtils'
import { LoggerName, namedLogger } from '../../../../utils/logger'

const logger = namedLogger(LoggerName.SpecialistAudio)

const MAX_SPECIALIST_DRIFT_SECONDS = (SCRUBBER_UPDATE_INTERVAL_MS * 2) / 1000

export type SpecialistAudioProxy = {
  /**
   * Updates the current time of the audio based on the overall ERT recording time.
   * @param relativeSeconds
   */
  currentTimeChanged: (relativeSeconds: number) => void

  play: (relativeSeconds: number) => void
  pause: () => void
  setSpeed: (speed: number) => void
  setVolume: (volume: number) => void
}

export type SpecialistAudioProps = {
  ref: Ref<SpecialistAudioProxy>
  audioRecording: ValidatedRecording
}

export const SpecialistAudio: FC<SpecialistAudioProps> = ({ ref, audioRecording }) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const pausedRef = useRef<boolean>(true)
  const disabledRef = useRef<boolean>(true)
  const [url, setUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    audioRecording.segments.map(segment => segment.data)
    const blobUrl = URL.createObjectURL(
      new Blob(
        audioRecording.segments.map(segment => segment.data),
        { type: 'audio/webm' }
      )
    )
    logger.debug('Created blob url for specialist audio recording:', blobUrl, {
      console: {
        audioRecording
      }
    })

    // Previously suppressed `react-hooks/set-state-in-effect` here. Biome 1.9.4 has no equivalent rule, and this effect-owned blob URL state is paired with cleanup below.
    setUrl(blobUrl) // I did this in an effect so that I could clean it up in a way that is easy to follow

    return () => {
      URL.revokeObjectURL(blobUrl)
      logger.debug('Revoked blob url', blobUrl)
    }
  }, [audioRecording])

  useImperativeHandle(ref, () => {
    return {
      currentTimeChanged: (relativeSeconds: number) => {
        const audioEl = audioRef.current
        if (audioEl) {
          const newCurrentTime = relativeSeconds - audioRecording.relativeStartTimeSeconds

          if (disabledRef.current) {
            if (!audioEl.paused) {
              audioEl.pause()
            }
          } else if (newCurrentTime > 0 && newCurrentTime < audioEl.duration) {
            const currentTime = audioEl.currentTime
            const drift = Math.abs(currentTime - newCurrentTime)
            if (drift > MAX_SPECIALIST_DRIFT_SECONDS) {
              audioEl.currentTime = newCurrentTime
            }
            if (!pausedRef.current && audioEl.paused) {
              audioEl.play()
            }
            if (pausedRef.current && !audioEl.paused) {
              audioEl.pause()
            }
          } else {
            audioEl.currentTime = 0
            if (!audioEl.paused) {
              audioEl.pause()
            }
          }
        }
      },

      play: (relativeSeconds: number) => {
        pausedRef.current = false
        const audioEl = audioRef.current

        if (audioEl && !disabledRef.current) {
          const newCurrentTime = relativeSeconds - audioRecording.relativeStartTimeSeconds
          if (newCurrentTime > 0 && newCurrentTime < audioEl.duration) {
            audioEl.play()
          }
        }
      },

      pause: () => {
        pausedRef.current = true
        if (audioRef.current) {
          audioRef.current.pause()
        }
      },

      setSpeed: (speed: number) => {
        if (audioRef.current) {
          audioRef.current.playbackRate = speed
        }
      },

      setVolume: (volume: number) => {
        if (audioRef.current) {
          audioRef.current.volume = volume

          // if the audio is muted then we consider it disabled
          disabledRef.current = volume <= 0

          if (disabledRef.current) {
            // if disabled, pause the audio
            if (!audioRef.current?.paused) {
              audioRef.current?.pause()
            }
          } else {
            // if not disabled, and no one paused it, play the audio
            if (!pausedRef.current) {
              audioRef.current?.play()
            }
          }
        }
      }
    } satisfies SpecialistAudioProxy
  }, [audioRecording.relativeStartTimeSeconds])

  if (url) {
    return <audio src={url} ref={audioRef} controls={isErtDebugModeEnabled()} />
  } else {
    return null
  }
}
